import { createSlice } from "@reduxjs/toolkit";
import { DATE_FORMAT } from "common/constants";
import { loadStateWithKey, saveStateWithKey } from "common/redux/redux-utils";
import { accountsApi as agencyAccountsApi } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { accountsApi as creditorAccountsApi } from "features/accountsTable/creditorPortal/accountsTableAPI";
import moment from "moment-timezone";

export const LOCAL_STORAGE_KEY = "state:accountsTable";

const saveState = saveStateWithKey(LOCAL_STORAGE_KEY, [
  "prevToken",
  "nextToken",
  "orderBy",
  "pageSize",
  "virtualPage",
  "selectedRows",
  "filteredAccounts",
  "resultCount",
  "resultNextToken",
  "resultPrevToken",
  "totalCount",
]);

export const INITIAL_PAGE_SIZE = 100;
export const INITIAL_ORDER_BY = null;
export const INITIAL_WORKLIST_ID = null;

// This state consists of variables that are needed for request and response
export const initialState = {
  uniqueDebtors: [],
  selectedRows: [],
  isShowDailyQueue: false,
  isShowPriorityQueue: false,
  // These are prevToken and nextToken that we use for request
  prevToken: null,
  nextToken: null,
  // These are prevToken and nextToken that we use for response
  resultPrevToken: null,
  resultNextToken: null,
  pageSize: null, // Set explicitly to null so we know it's the first time loading the page
  resultCount: 0,
  filteredAccounts: [],
  orderBy: INITIAL_ORDER_BY,
  totalCount: null,
  // Cursor pagination doesn't have a real page. We are showing "fake" or "virtual" page to the end user
  virtualPage: 1,
  worklistId: INITIAL_WORKLIST_ID,
  filters: {
    includeStatusExternalNames: [],
    excludeStatusExternalNames: [],
    minExternalScore: null,
    maxExternalScore: null,
    minAccountBalance: null,
    maxAccountBalance: null,
    minIntelitechRScore: null,
    maxIntelitechRScore: null,
    minIntelitechQScore: null,
    maxIntelitechQScore: null,
    minIntelitechFPGScore: null,
    maxIntelitechFPGScore: null,
    minIntelitechIScore: null,
    maxIntelitechIScore: null,
    includeIntelitechMedalScores: null,
    excludeIntelitechMedalScores: null,
    followUpStartDate: null,
    followUpEndDate: null,
    uploadDateStart: null,
    uploadDateEnd: null,
    includeTurnoverDateStart: null,
    includeTurnoverDateEnd: null,
    excludeTurnoverDateStart: null,
    excludeTurnoverDateEnd: null,
    includeOpenDateStart: null,
    includeOpenDateEnd: null,
    excludeOpenDateStart: null,
    excludeOpenDateEnd: null,
    includeLastEmailDateStart: null,
    includeLastEmailDateEnd: null,
    excludeLastEmailDateStart: null,
    excludeLastEmailDateEnd: null,
    includeLastCallDateStart: null,
    includeLastCallDateEnd: null,
    excludeLastCallDateStart: null,
    excludeLastCallDateEnd: null,
    includeLastLetterDateStart: null,
    includeLastLetterDateEnd: null,
    excludeLastLetterDateStart: null,
    excludeLastLetterDateEnd: null,
    includeLastPaymentDateStart: null,
    includeLastPaymentDateEnd: null,
    excludeLastPaymentDateStart: null,
    excludeLastPaymentDateEnd: null,
    includeAccountCustomFieldFilters: [],
    excludeAccountCustomFieldFilters: [],
    includeDebtorCustomFieldFilters: [],
    excludeDebtorCustomFieldFilters: [],
    excludeStatusCodes: [],
    includeStatusCodes: [],
    excludeDeskIds: [],
    includeDeskIds: [],
    debtorStates: [],
    debtorTagIds: [],
    assigneeIds: [],
    creditorIds: [],
    creditorTagIds: [],
    originalCreditorNames: [],
    hasLawsuit: null,
    collectionDebtTypes: null,
  },
};

// We want to reset some properties
export const loadAccountsTableSlice = () => ({
  ...initialState,
  ...loadStateWithKey(LOCAL_STORAGE_KEY),
});

export const accountsTableSlice = createSlice({
  name: "accountsTable",
  initialState,
  reducers: {
    setFilterIncludeAccountCustomFields: (state, action) => {
      state.filters.includeAccountCustomFieldFilters = action.payload || [];
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeAccountCustomFields: (state, action) => {
      state.filters.excludeAccountCustomFieldFilters = action.payload || [];
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeDebtorCustomFields: (state, action) => {
      state.filters.includeDebtorCustomFieldFilters = action.payload || [];
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeDebtorCustomFields: (state, action) => {
      state.filters.excludeDebtorCustomFieldFilters = action.payload || [];
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setDailyQueueAccounts: (state, action) => {
      state.isShowDailyQueue = action.payload;
      state.filters.followUpStartDate = null;
      state.filters.followUpEndDate = null;
      if (state.isShowDailyQueue) {
        state.filters.followUpEndDate = moment().startOf("day").format(DATE_FORMAT);
      }
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setHasLawsuit: (state, action) => {
      state.filters.hasLawsuit = action.payload ? true : null;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setCollectionDebtTypes: (state, action) => {
      state.filters.collectionDebtTypes = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setPriorityQueueAccounts: (state, action) => {
      state.isShowPriorityQueue = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setDebtorStates: (state, action) => {
      state.filters.debtorStates = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setDebtorTagIds: (state, action) => {
      state.filters.debtorTagIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setAssigneeIds: (state, action) => {
      state.filters.assigneeIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setCreditorIds: (state, action) => {
      state.filters.creditorIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setCreditorTagIds: (state, action) => {
      state.filters.creditorTagIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setOriginalCreditorNames: (state, action) => {
      state.filters.originalCreditorNames = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setSelectedRows: (state, action) => {
      state.selectedRows = action.payload;
      saveState(state);
    },
    selectRows: (state, action) => {
      state.selectedRows = [...state.selectedRows, ...action.payload];
      saveState(state);
    },
    setFilterMinAccountScore: (state, action) => {
      state.filters.minExternalScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxAccountScore: (state, action) => {
      state.filters.maxExternalScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMinAccountBalance: (state, action) => {
      state.filters.minAccountBalance = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxAccountBalance: (state, action) => {
      state.filters.maxAccountBalance = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMinIntelitechIScore: (state, action) => {
      state.filters.minIntelitechIScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxIntelitechIScore: (state, action) => {
      state.filters.maxIntelitechIScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeIntelitechMedalScores: (state, action) => {
      state.filters.includeIntelitechMedalScores = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeIntelitechMedalScores: (state, action) => {
      state.filters.excludeIntelitechMedalScores = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMinIntelitechRScore: (state, action) => {
      state.filters.minIntelitechRScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxIntelitechRScore: (state, action) => {
      state.filters.maxIntelitechRScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMinIntelitechQScore: (state, action) => {
      state.filters.minIntelitechQScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxIntelitechQScore: (state, action) => {
      state.filters.maxIntelitechQScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMinIntelitechFPGScore: (state, action) => {
      state.filters.minIntelitechFPGScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterMaxIntelitechFPGScore: (state, action) => {
      state.filters.maxIntelitechFPGScore = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterFollowUpDates: (state, action) => {
      if (!action.payload) {
        state.filters.followUpStartDate = null;
        state.filters.followUpEndDate = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.followUpStartDate, state.filters.followUpEndDate] = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterUploadDates: (state, action) => {
      if (!action.payload) {
        state.filters.uploadDateStart = null;
        state.filters.uploadDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.uploadDateStart, state.filters.uploadDateEnd] = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeTurnoverDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeTurnoverDateStart = null;
        state.filters.includeTurnoverDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeTurnoverDateStart, state.filters.includeTurnoverDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeTurnoverDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeTurnoverDateStart = null;
        state.filters.excludeTurnoverDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeTurnoverDateStart, state.filters.excludeTurnoverDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeOpenDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeOpenDateStart = null;
        state.filters.includeOpenDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeOpenDateStart, state.filters.includeOpenDateEnd] = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeOpenDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeOpenDateStart = null;
        state.filters.excludeOpenDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeOpenDateStart, state.filters.excludeOpenDateEnd] = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeLastEmailDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeLastEmailDateStart = null;
        state.filters.includeLastEmailDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeLastEmailDateStart, state.filters.includeLastEmailDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeLastEmailDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeLastEmailDateStart = null;
        state.filters.excludeLastEmailDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeLastEmailDateStart, state.filters.excludeLastEmailDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeLastCallDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeLastCallDateStart = null;
        state.filters.includeLastCallDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeLastCallDateStart, state.filters.includeLastCallDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeLastCallDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeLastCallDateStart = null;
        state.filters.excludeLastCallDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeLastCallDateStart, state.filters.excludeLastCallDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeLastLetterDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeLastLetterDateStart = null;
        state.filters.includeLastLetterDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeLastLetterDateStart, state.filters.includeLastLetterDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeLastLetterDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeLastLetterDateStart = null;
        state.filters.excludeLastLetterDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeLastLetterDateStart, state.filters.excludeLastLetterDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeLastPaymentDates: (state, action) => {
      if (!action.payload) {
        state.filters.includeLastPaymentDateStart = null;
        state.filters.includeLastPaymentDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.includeLastPaymentDateStart, state.filters.includeLastPaymentDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeLastPaymentDates: (state, action) => {
      if (!action.payload) {
        state.filters.excludeLastPaymentDateStart = null;
        state.filters.excludeLastPaymentDateEnd = null;
        state.prevToken = null;
        state.nextToken = null;
        state.virtualPage = 1;
        saveState(state);
        return;
      }
      [state.filters.excludeLastPaymentDateStart, state.filters.excludeLastPaymentDateEnd] =
        action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeStatusCodes: (state, action) => {
      state.filters.includeStatusCodes = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeStatusCodes: (state, action) => {
      state.filters.excludeStatusCodes = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeDeskIds: (state, action) => {
      state.filters.includeDeskIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeDeskIds: (state, action) => {
      state.filters.excludeDeskIds = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterIncludeStatusExternalNames: (state, action) => {
      state.filters.includeStatusExternalNames = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setFilterExcludeStatusExternalNames: (state, action) => {
      state.filters.excludeStatusExternalNames = action.payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setPagination: (state, { payload }) => {
      state.pageSize = payload.pageSize;
      state.prevToken = payload.prevToken;
      state.nextToken = payload.nextToken;
      state.virtualPage = payload.virtualPage;
      saveState(state);
    },
    setOrderBy: (state, { payload }) => {
      state.orderBy = payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    setWorklistId: (state, { payload }) => {
      state.worklistId = payload;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      saveState(state);
    },
    resetFilters: (state, action) => {
      state.filters = initialState.filters;
      state.isShowDailyQueue = initialState.isShowDailyQueue;
      state.prevToken = null;
      state.nextToken = null;
      state.virtualPage = 1;
      state.orderBy = INITIAL_ORDER_BY;
      saveState(state);
    },
    setVirtualPage: (state, { payload }) => {
      state.virtualPage = payload;
      saveState(state);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      agencyAccountsApi.endpoints.fetchWorklistAccounts.matchFulfilled,
      (state, { payload }) => {
        state.uniqueDebtors = payload.results.reduce((acc, account) => {
          if (!acc.includes(account.debtorId)) {
            acc.push(account.debtorId);
          }
          return acc;
        }, []);
        state.filteredAccounts = payload.results;
        state.resultCount = payload.meta.resultCount;
        state.resultPrevToken = payload.meta.prevToken;
        state.resultNextToken = payload.meta.nextToken;
        saveState(state);
      },
    );
    builder.addMatcher(
      agencyAccountsApi.endpoints.fetchAccounts.matchFulfilled,
      (state, { payload }) => {
        state.filteredAccounts = payload.results;
        state.resultCount = payload.meta.resultCount;
        state.resultPrevToken = payload.meta.prevToken;
        state.resultNextToken = payload.meta.nextToken;
        saveState(state);
      },
    );

    builder.addMatcher(
      creditorAccountsApi.endpoints.fetchCreditorPortalAccounts.matchFulfilled,
      (state, { payload }) => {
        state.filteredAccounts = payload.results;
        state.resultCount = payload.meta.resultCount;
        state.resultPrevToken = payload.meta.prevToken;
        state.resultNextToken = payload.meta.nextToken;
        saveState(state);
      },
    );

    builder.addMatcher(
      creditorAccountsApi.endpoints.fetchCreditorPortalWorklistAccounts.matchFulfilled,
      (state, { payload }) => {
        state.filteredAccounts = payload.results;
        state.resultCount = payload.meta.resultCount;
        state.resultPrevToken = payload.meta.prevToken;
        state.resultNextToken = payload.meta.nextToken;
        saveState(state);
      },
    );
  },
});

export const selectAccountsTableSlice = (state) => state.accountsTable;

export const {
  setDailyQueueAccounts,
  setPriorityQueueAccounts,
  setSelectedRows,
  selectRows,
  setFilterMinAccountBalance,
  setFilterMaxAccountBalance,
  setFilterMinAccountScore,
  setFilterMaxAccountScore,
  setFilterMinIntelitechIScore,
  setFilterMaxIntelitechIScore,
  setFilterIncludeIntelitechMedalScores,
  setFilterExcludeIntelitechMedalScores,
  setFilterMinIntelitechRScore,
  setFilterMaxIntelitechRScore,
  setFilterMinIntelitechQScore,
  setFilterMaxIntelitechQScore,
  setFilterMinIntelitechFPGScore,
  setFilterMaxIntelitechFPGScore,
  setFilterFollowUpDates,
  setFilterUploadDates,
  setFilterIncludeTurnoverDates,
  setFilterExcludeTurnoverDates,
  setFilterIncludeOpenDates,
  setFilterExcludeOpenDates,
  setFilterIncludeLastEmailDates,
  setFilterExcludeLastEmailDates,
  setFilterIncludeLastCallDates,
  setFilterExcludeLastCallDates,
  setFilterIncludeLastLetterDates,
  setFilterExcludeLastLetterDates,
  setFilterIncludeLastPaymentDates,
  setFilterExcludeLastPaymentDates,
  setFilterExcludeStatusCodes,
  setFilterIncludeStatusCodes,
  setFilterExcludeDeskIds,
  setFilterIncludeDeskIds,
  setFilterExcludeStatusExternalNames,
  setFilterIncludeStatusExternalNames,
  setPagination,
  setOrderBy,
  resetFilters,
  setVirtualPage,
  setHasLawsuit,
  setCollectionDebtTypes,
  setAssigneeIds,
  setDebtorStates,
  setDebtorTagIds,
  setCreditorIds,
  setCreditorTagIds,
  setOriginalCreditorNames,
  setWorklistId,
  setFilterIncludeAccountCustomFields,
  setFilterExcludeAccountCustomFields,
  setFilterIncludeDebtorCustomFields,
  setFilterExcludeDebtorCustomFields,
} = accountsTableSlice.actions;

export default accountsTableSlice.reducer;
